import React, { useEffect, useState } from 'react';
import { supabase } from './supabase';
import {
    Clock,
    User,
    Activity,
    Scissors,
    ArrowRight,
    CreditCard,
    MessageSquare,
    Trash2,
    AlertTriangle,
    FileText,
    Search
} from 'lucide-react';
import './ActivityLog.css';

interface ActivityLogItem {
    id: number;
    user_email: string;
    action: string;
    details: any;
    created_at: string;
}

interface OrderInfo {
    tiendanube_id: string | null;
    source: string | null;
}

export function ActivityLog() {
    const [logs, setLogs] = useState<ActivityLogItem[]>([]);
    const [orderMap, setOrderMap] = useState<Record<number, OrderInfo>>({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchLogs();
    }, []);

    async function fetchLogs() {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('activity_logs')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(100);

            if (error) throw error;
            const rows = data || [];
            setLogs(rows);

            const ids = new Set<number>();
            rows.forEach((r: ActivityLogItem) => {
                const oid = r.details?.order_id ?? r.details?.id;
                if (typeof oid === 'number') ids.add(oid);
            });
            if (ids.size > 0) {
                const idList = Array.from(ids);
                const { data: orders } = await supabase
                    .from('orders')
                    .select('id, tiendanube_id, source')
                    .in('id', idList);
                const map: Record<number, OrderInfo> = {};
                (orders || []).forEach((o: any) => {
                    map[o.id] = { tiendanube_id: o.tiendanube_id, source: o.source };
                });
                setOrderMap(map);
            }
        } catch (err) {
            console.error('Error fetching activity logs:', err);
        } finally {
            setLoading(false);
        }
    }

    function formatTime(dateStr: string) {
        return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    function groupLogsByDate(logs: ActivityLogItem[]) {
        const groups: { [key: string]: ActivityLogItem[] } = {};
        logs.forEach(log => {
            const date = new Date(log.created_at).toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(log);
        });
        return groups;
    }

    const getOrderReference = (details: any) => {
        if (!details) return 'Desconocida';
        const internalId: number | undefined = details.order_id ?? details.id;
        const info = typeof internalId === 'number' ? orderMap[internalId] : undefined;
        const tnId = info?.tiendanube_id || details.tiendanube_id || details.tiendanubeId || null;
        const source = info?.source || details.source || null;

        if (tnId) {
            if (source === 'woocommerce') return `#BT-${tnId}`;
            if (source === 'mercadolibre' || source === 'meli') return `#ML-${tnId}`;
            return `#${tnId}`;
        }
        if (typeof internalId === 'number') return `K-${internalId}`;
        return 'Sin ID';
    };

    const getActionConfig = (log: ActivityLogItem) => {
        const { action, details } = log;
        const orderRef = getOrderReference(details);

        switch (action) {
            case 'ORDEN_CORTADA_AUTO':
                return {
                    icon: <Scissors size={18} />,
                    title: `Orden ${orderRef} Cortada Automáticamente`,
                    description: 'Todos los productos fueron marcados como cortados.',
                    color: 'purple'
                };
            case 'PRODUCTO_CORTADO':
                return {
                    icon: <Scissors size={18} />,
                    title: `Producto Cortado: ${details?.product_name || 'Desconocido'}`,
                    description: (
                        <div className="log-action-detail">
                            <span className="log-order-ref">{orderRef}</span> - {details?.is_cut ? 'Marcado como cortado.' : 'Desmarcado como cortado.'}
                        </div>
                    ),
                    color: 'blue'
                };
            case 'CAMBIO_ESTADO':
            case 'CAMBIO_ESTADO_MOVIDO':
                return {
                    icon: <Activity size={18} />,
                    title: `Cambio de Estado: ${orderRef}`,
                    description: (
                        <div className="status-change">
                            <span className={`status-badge ${details?.old_status}`}>{details?.old_status || '---'}</span>
                            <ArrowRight size={14} className="arrow-icon" />
                            <span className={`status-badge ${details?.new_status}`}>{details?.new_status || '---'}</span>
                            {details?.note && <div className="log-action-detail" style={{ width: '100%', marginTop: 4 }}>{details.note}</div>}
                        </div>
                    ),
                    color: 'orange'
                };
            case 'CAMBIO_PAGO':
                return {
                    icon: <CreditCard size={18} />,
                    title: `Pago Actualizado: ${orderRef}`,
                    description: (
                        <div>
                            Estado: <span className={`status-badge ${details?.new_payment_status === 'paid' ? 'pago' : 'pendiente'}`}>
                                {details?.new_payment_status === 'paid' ? 'PAGADO' : 'PENDIENTE'}
                            </span>
                        </div>
                    ),
                    color: 'green'
                };
            case 'NOTA_INTERNA':
                return {
                    icon: <MessageSquare size={18} />,
                    title: `Nota Interna: ${orderRef}`,
                    description: <blockquote className="log-quote">"{details?.note}"</blockquote>,
                    color: 'yellow'
                };
            case 'ELIMINAR_ORDEN':
                return {
                    icon: <Trash2 size={18} />,
                    title: `Orden Eliminada: ${orderRef}`,
                    description: `Cliente: ${details?.contact_name || 'N/A'}`,
                    color: 'red'
                };
            default:
                // Cambios con explicación (ej. los que se hacen desde el servidor o por pedido, 22/10/2026):
                // título legible + la nota; el JSON queda colapsado debajo.
                if (details?.note) {
                    return {
                        icon: <FileText size={18} />,
                        title: `${action.charAt(0) + action.slice(1).toLowerCase().replace(/_/g, ' ')}${details?.order_id || details?.tiendanube_id ? `: ${orderRef}` : ''}`,
                        description: (
                            <div className="log-action-detail">
                                {details.note}
                                <details className="raw-json-details">
                                    <summary className="json-summary">Ver detalles</summary>
                                    <pre className="raw-json-content">{JSON.stringify(details, null, 2)}</pre>
                                </details>
                            </div>
                        ),
                        color: 'blue'
                    };
                }
                return {
                    icon: <FileText size={18} />,
                    title: action.replace(/_/g, ' '),
                    description: (
                        <details className="raw-json-details">
                            <summary className="json-summary">Ver detalles JSON</summary>
                            <pre className="raw-json-content">
                                {JSON.stringify(details, null, 2)}
                            </pre>
                        </details>
                    ),
                    color: 'gray'
                };
        }
    };

    const filteredLogs = logs.filter(log => {
        const search = searchTerm.toLowerCase();
        const config = getActionConfig(log);
        // Helper to safely check strings
        const check = (str: any) => str && str.toString().toLowerCase().includes(search);

        return (
            check(config.title) ||
            check(log.action) ||
            (log.user_email && check(log.user_email)) ||
            (typeof config.description === 'string' && check(config.description))
        );
    });

    const groupedLogs = groupLogsByDate(filteredLogs);

    return (
        <div className="activity-log-container">
            <div className="activity-header">
                <div>
                    <h2><Activity size={24} /> Historial de Actividad</h2>
                    <span className="subtitle">Últimos movimientos del sistema</span>
                </div>
                <div className="activity-search">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Buscar actividad..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {loading ? (
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Cargando actividad...</p>
                </div>
            ) : (
                <div className="timeline">
                    {Object.keys(groupedLogs).length === 0 ? (
                        <div className="empty-state">
                            <Activity size={48} className="empty-icon" />
                            <p>No hay actividad reciente.</p>
                        </div>
                    ) : (
                        Object.entries(groupedLogs).map(([date, dayLogs]: [string, ActivityLogItem[]]) => (
                            <div key={date} className="timeline-group">
                                <div className="timeline-date">
                                    <span>{date}</span>
                                </div>
                                <div className="timeline-items">
                                    {dayLogs.map((log: ActivityLogItem) => {
                                        const config = getActionConfig(log);
                                        return (
                                            <div key={log.id} className={`timeline-item ${config.color}`}>
                                                <div className="timeline-time">{formatTime(log.created_at)}</div>
                                                <div className="timeline-marker">
                                                    <div className="marker-icon">{config.icon}</div>
                                                    <div className="marker-line"></div>
                                                </div>
                                                <div className="timeline-content">
                                                    <div className="log-title">{config.title}</div>
                                                    <div className="log-description">{config.description}</div>
                                                    <div className="log-meta">
                                                        <User size={12} />
                                                        <span>{log.user_email || 'Sistema'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
